import { keepPreviousData } from '@tanstack/react-query';
// ConfigEditorModal removed — Studio is the only editor
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
// Command components removed - using DesignPicker now
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
// Popover removed - using DesignPicker now
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
// Tabs removed - using CollapsibleSections now
import { useToast } from '@/components/ui/use-toast';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { useStore } from '@/lib/api/stores';
import { trpc } from '@/lib/api/trpc';
import { cn } from '@/lib/utils';
import {
  ArrowLeft,
  Ban,
  Code2,
  Copy,
  CreditCard,
  Database,
  Loader2,
  MapPin,
  Settings2,
  ShoppingBag,
  Split,
  Target,
  Truck,
  Zap,
} from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams as useRouterSearchParams } from 'react-router-dom';
import { PluginMarketplaceV2 } from '../../plugins/components/PluginMarketplaceV2';
import { STEP_ICONS, STEP_TYPE_LABELS } from '../constants/stepStyles';
import type { StepType, PaymentMethodEntry } from '../types';
import { FlowStep, StepVariant } from '../types';
import { generateSmartPath, shouldGenerateSmartPath } from '../utils/pathGenerator';
import { CollapsibleSection } from './CollapsibleSection';
import { DesignPicker, type DesignOption } from './DesignPicker';
// EditorSectionBlock removed - using CollapsibleSection now
import { ExternalTrackerSnippet } from './ExternalTrackerSnippet';
import { ManageVariantsDialog } from './ManageVariantsDialog';
import { PathEditor } from './PathEditor';
import { OrderBumpStepConfig } from './OrderBumpStepConfig';
import { CheckoutOfferStepConfig } from './CheckoutOfferStepConfig';

import { PaymentSetupConfigEditor } from './PaymentSetupConfigEditor';
import { ShippingRatesStepConfig } from './ShippingRatesStepConfig';
import { AccessRestrictionsStepConfig } from './AccessRestrictionsStepConfig';
import { AddressFormStepConfig } from './AddressFormStepConfig';
import { PaymentSettingsStepConfig } from './PaymentSettingsStepConfig';
import {
  countMetaScriptConflicts,
  type ManagedMetaPixel,
} from '@/lib/tracking/meta-script-conflict';
import { ScriptsEditor } from './ScriptsEditor';
// StepResourcesEditor removed - using StepStaticResourcesEditor only
import { StepStaticResourcesEditor } from './StepStaticResourcesEditor';
import { TrackingConfigurationPanel } from './tracking/TrackingConfigurationPanel';

/**
 * `/thankyou/:orderId` → `/thankyou`. Config-side paths may be decorated with
 * routing params; deployment-side paths never are, so strip before comparing.
 */
const stripRouteParams = (p: string | undefined) => (p ?? '').replace(/\/:[^/]+/g, '');

interface StepEditorPanelProps {
  step: FlowStep;
  allSteps?: FlowStep[]; // All steps in the funnel (for connection selection)
  onClose: () => void;
  onChange: (step: FlowStep) => void;
  onTransformToSplit: (variants: StepVariant[], splitType: 'weighted' | 'geo') => void;
  onUpdateVariants: (variants: StepVariant[], splitType?: 'weighted' | 'geo') => void;
  onRemoveSplit?: (variantToKeep: StepVariant) => void;
  templatesWithIntegrationStatus?: any[]; // List of templates with their availability status (for integration-based features)
  funnelId?: string;
  // Note: staticResources now stored in stepConfig.staticResources, no longer passed as props
  scripts?: Record<string, string | Record<string, string>>; // Scripts state from parent (stepId -> script content, or stepId -> variantId -> script content for splits)
  onScriptsChange?: (
    scripts:
      | Record<string, string | Record<string, string>>
      | ((
        prev: Record<string, string | Record<string, string>>,
      ) => Record<string, string | Record<string, string>>),
  ) => void; // Update scripts
  isLoadingScripts?: boolean; // Loading state for scripts (optional, defaults to false)
  initialTab?: string; // Optional: Initial tab to open (e.g., 'conditions' from edge click)
  /** Called when user wants to open the visual editor (Studio iframe) for this step */
  onOpenVisualEditor?: (stepId: string, pluginId?: string, deploymentId?: string, pagePath?: string) => void;
  /** Called when user wants to create a new design from scratch with Studio */
  onCreateWithStudio?: (stepId: string) => void;
  /** Centralized edit handler — routes to the correct editor (Studio, external, or config editor) */
  onEditDesign?: (stepId: string, designInfo?: { pluginId?: string; deploymentId?: string; pagePath?: string }, variantId?: string) => void;
  /** Called when user wants to duplicate a design */
  onDuplicateDesign?: (stepId: string) => void;
  /** A preview screenshot capture is in flight for this step (drives the
   * DESIGNS card "Capturing…" state while the active design has no thumbnail). */
  isCapturing?: boolean;
  /** Copy this step's pixel config to every other step (TAG-1213).
   * Returns false when the step has no pixels to copy. */
  onCopyPixelsToAllSteps?: () => boolean;
  /** The funnel has a production config, so a design switch here reaches live shoppers on the next publish. */
  isFunnelPublished?: boolean;
}

// Note: STEP_ICONS is now imported from ../constants/stepStyles

/**
 * Format regions summary for display in variant tabs
 * Shows first 3 country codes, or "X countries" if more
 * Returns "No regions" if empty (for geo splits)
 */
function formatRegionsSummary(regions?: string[], t?: (key: string, opts?: any) => string): string {
  if (!regions || regions.length === 0) {
    return t ? t('stepEditor.noRegions') : 'No regions';
  }

  if (regions.length <= 3) {
    return regions.join(', ');
  }

  return t ? t('stepEditor.countriesCount', { count: regions.length }) : `${regions.length} countries`;
}

/**
 * Professional Step Editor with tabbed interface for better ergonomics
 */
export function StepEditorPanel({
  step,
  allSteps = [],
  onClose,
  onChange,
  onTransformToSplit,
  onUpdateVariants,
  onRemoveSplit,
  templatesWithIntegrationStatus,
  funnelId,
  scripts = {},
  onScriptsChange = () => { },
  isLoadingScripts = false,
  initialTab,
  onOpenVisualEditor,
  onCreateWithStudio,
  onEditDesign,
  onDuplicateDesign,
  isCapturing = false,
  onCopyPixelsToAllSteps,
  isFunnelPublished,
}: StepEditorPanelProps) {
  const { t } = useTranslation('storefront');
  const { id } = useStoreNavigate();
  const designSectionRef = React.useRef<HTMLDivElement>(null);
  const [designHighlighted, setDesignHighlighted] = React.useState(false);

  const [searchParams, setSearchParams] = useRouterSearchParams();
  const { toast } = useToast();
  const utils = trpc.useUtils();

  // tRPC mutation for creating builder sessions


  // Fetch store data to get store type for entry source configuration
  const { data: store } = useStore(id!);

  // Fetch integrations to derive default pixel configuration from store-level settings


  // Check if this is a split step
  const isSplitStep = !!step.variants && step.variants.length > 0;

  // Track state - use initialTab prop if provided, then URL param, then default
  const [activeTab, setActiveTab] = React.useState<string>(() => {
    return initialTab || searchParams.get('tab') || 'config';
  });
  React.useEffect(() => {
    if (!initialTab) return;
    const baseTab = initialTab.split('_')[0];
    setActiveTab(baseTab);
  }, [initialTab]);

  React.useEffect(() => {
    if (!initialTab?.startsWith('design')) return;
    setDesignHighlighted(true);
    const scrollTimer = setTimeout(() => {
      designSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
    const clearTimer = setTimeout(() => setDesignHighlighted(false), 2500);
    return () => { clearTimeout(scrollTimer); clearTimeout(clearTimer); };
  }, [initialTab]);

  const [selectedVariantIndex, setSelectedVariantIndex] = React.useState(() => {
    // Initialize from URL if available
    const variantParam = searchParams.get('variant');
    if (variantParam && isSplitStep) {
      const index = parseInt(variantParam, 10);
      if (!isNaN(index) && index >= 0 && index < (step.variants?.length || 0)) {
        return index;
      }
    }
    return 0;
  });

  // Clamp variant index when variants are removed
  React.useEffect(() => {
    if (isSplitStep && step.variants && selectedVariantIndex >= step.variants.length) {
      setSelectedVariantIndex(Math.max(0, step.variants.length - 1));
    }
  }, [isSplitStep, step.variants?.length, selectedVariantIndex]);

  // Local draft for the step label (FE-10): committing per keystroke ran the
  // whole-funnel remap + stringify-diff + canvas fingerprint pipeline per
  // character. Commit on blur/Enter instead (same pattern as the canvas
  // inline label editor).
  const [labelDraft, setLabelDraft] = React.useState<string | null>(null);
  React.useEffect(() => { setLabelDraft(null); }, [step.id]);

  const [instanceToDiscard, setInstanceToDiscard] = React.useState<string | null>(null);
  const [marketplaceDialogOpen, setMarketplaceDialogOpen] = React.useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = React.useState(false);
  const [instanceToRename, setInstanceToRename] = React.useState<{
    instanceId: string;
    currentName: string;
  } | null>(null);
  const [newDesignName, setNewDesignName] = React.useState('');

  // Validate activeTab when step changes (ensure tab is valid for this step type)
  React.useEffect(() => {
    const validTabs = ['config', 'scripts', 'trigger', 'pixel'];
    if (step.type === 'checkout') {
      validTabs.push('payment');
    }

    // If current tab is invalid for this step, reset to 'config'
    if (!validTabs.includes(activeTab)) {
      setActiveTab('config');
    }
  }, [step.id, step.type, activeTab]);

  // Sync activeTab state → URL (when user clicks tabs)
  const updateUrlTab = React.useCallback(
    (tab: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (tab !== 'config') {
        newParams.set('tab', tab);
      } else {
        newParams.delete('tab');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Handle tab change (update both state and URL)
  const handleTabChange = React.useCallback(
    (tab: string) => {
      setActiveTab(tab);
      updateUrlTab(tab);
    },
    [updateUrlTab],
  );

  // Sync selectedVariantIndex with URL (only for split steps)
  React.useEffect(() => {
    if (!isSplitStep) return;

    const currentVariant = searchParams.get('variant');
    const variantString = selectedVariantIndex.toString();

    if (currentVariant !== variantString) {
      const newParams = new URLSearchParams(searchParams);
      if (selectedVariantIndex !== 0) {
        newParams.set('variant', variantString);
      } else {
        newParams.delete('variant');
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [selectedVariantIndex, isSplitStep]);

  // Fetch data (needed for both standard and split steps, but not for external steps)
  // Use stale-while-revalidate: show cached data immediately, refresh in background
  const {
    data: instancesData,
    isLoading: isDraftsLoading,
    isFetching: isRefreshingInstances,
  } =
    trpc.plugins.getAvailableInstancesForStepType.useQuery(
      step.type !== 'external'
        ? { storeId: id || '', stepType: step.type as any }
        : { storeId: id || '', stepType: 'custom' }, // Fallback for external type
      {
        enabled: !!id && !!step.type && step.type !== 'external',
        refetchOnMount: true, // Respects staleTime - only refetches if data is stale
        refetchOnWindowFocus: false, // Don't refetch on window focus to avoid interruptions
        staleTime: 30 * 1000, // Consider data fresh for 30 seconds (instant return from editor)
        placeholderData: keepPreviousData, // Keep showing previous data during refetch (no flicker)
      },
    );

  const {
    data: availableConditions,
    isLoading: isLoadingConditions,
    isFetching: isRefreshingConditions,
  } =
    trpc.funnels.getAvailableConditions.useQuery(undefined, {
      refetchOnMount: true, // Respects staleTime
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // Conditions rarely change - 5 min staleTime
      placeholderData: keepPreviousData, // Keep showing previous data during refetch
    });

  // forkForEditMutation removed — Studio is the only editor now

  // Track if we should show force delete option
  const [forceDeleteInfo, setForceDeleteInfo] = React.useState<{ instanceId: string; locations: string } | null>(null);

  // Remove instance mutation (for discard)
  const removeInstanceMutation = trpc.plugins.removeInstance.useMutation({
    onSuccess: () => {
      utils.plugins.getAvailableInstancesForStepType.invalidate();
      setInstanceToDiscard(null);
      setForceDeleteInfo(null);

      // Clear the instance from step config
      handleConfigChange({
        variant: undefined,
        instanceId: undefined,
      });

      toast({
        title: t('stepEditor.success'),
        description: t('stepEditor.designDeleted'),
      });
    },
    onError: (error: any) => {
      const message = error.message || t('stepEditor.failedDeleteDesign');
      const isInUseError = message.includes('currently in use');

      if (isInUseError && instanceToDiscard) {
        // Extract the locations and show force delete option
        const match = message.match(/currently in use at: (.+?)\. Please/);
        const locations = match ? match[1] : t('stepEditor.multipleLocations');
        setForceDeleteInfo({ instanceId: instanceToDiscard, locations });
        // Don't close the dialog — show force option
      } else {
        toast({
          title: t('stepEditor.deleteFailed'),
          description: message,
          variant: 'destructive',
        });
        setInstanceToDiscard(null);
      }
    },
  });

  const renameInstanceMutation = trpc.plugins.updateInstanceConfig.useMutation({
    onSuccess: () => {
      utils.plugins.getAvailableInstancesForStepType.invalidate();
      setRenameDialogOpen(false);
      setInstanceToRename(null);
      setNewDesignName('');

      toast({
        title: t('stepEditor.success'),
        description: t('stepEditor.designRenamed'),
      });
    },
    onError: (error: any) => {
      console.error('[renameInstance] Error:', error);
      toast({
        title: t('stepEditor.error'),
        description: error.message || t('stepEditor.failedRenameDesign'),
        variant: 'destructive',
      });
    },
  });

  // Only show loading skeleton on initial load (no cached data)
  // Background refreshes (isFetching) show a subtle indicator instead
  const isInitialLoading = (isDraftsLoading && !instancesData) || (isLoadingConditions && !availableConditions);
  const isRefreshing = isRefreshingInstances || isRefreshingConditions;

  // Legacy alias for compatibility - but now only true on initial load
  const isLoading = isInitialLoading;

  // Get config (works for both standard and split steps)
  const config = isSplitStep && step.variants ? step.variants[selectedVariantIndex]?.config : step.config;

  // Get current instance data (for showing internal path and building CDN URL)
  // Must match BOTH instanceId AND the selected page path
  const currentInstanceData = React.useMemo(() => {
    if (!config?.instanceId || !instancesData?.instances) return null;
    const selectedPath = config.metadata?.internalPath;

    // If we have a selected path, find instance matching both instanceId and path
    if (selectedPath) {
      return instancesData.instances.find(
        (inst: any) => inst.instanceId === config.instanceId && inst.path === selectedPath,
      );
    }

    // Fallback: just match by instanceId (for backward compatibility)
    return instancesData.instances.find((inst: any) => inst.instanceId === config.instanceId);
  }, [config?.instanceId, config?.metadata?.internalPath, instancesData]);

  // Check if current instance's path is remappable
  const getRemappableStatus = React.useCallback((instance: any) => {
    if (!instance) {
      return { remappable: false, reason: t('stepEditor.noDesignSelected') };
    }

    // The remappable flag is directly on the instance (from API response)
    if (instance.remappable === true) {
      return { remappable: true, reason: null };
    }

    return {
      remappable: false,
      reason: t('stepEditor.pathRemapNotSupported'),
    };
  }, []);

  const remappableStatus = currentInstanceData ? getRemappableStatus(currentInstanceData) : null;

  // Get current condition operator
  const getCurrentConditionOperator = (): string => {
    const when = step.conditions?.when;
    if (!when) return 'none';
    if (typeof when === 'string') return when;
    if (typeof when === 'object') {
      if ('operator' in when) return (when as any).operator;
      const keys = Object.keys(when);
      if (keys.length > 0) return keys[0];
    }
    return 'none';
  };

  const selectedConditionDef = availableConditions?.find(
    (c: any) => c.name === getCurrentConditionOperator(),
  );

  // Build available options from instancesData (NO PAGE FILTER - show all plugins for step type)
  let availablePlugins: {
    pluginId: string;
    pluginName: string;
    pathCount: number;
    hasDuplicateName?: boolean;
  }[] = [];
  let availablePages: { path: string; name?: string; remappable?: boolean; screenshots?: { desktop?: string; mobile?: string } }[] = [];
  let availableVersions: string[] = [];
  let availableConfigs: {
    configName: string;
    versions: string[];
    hasDrafts?: boolean;
    draftCount?: number;
  }[] = [];

  if (instancesData?.byPath) {
    // Get all unique plugins (across all paths for this step type)
    const pluginsMap = new Map<string, { pluginId: string; pluginName: string; paths: any[] }>();
    instancesData.byPath.forEach((pathData: any) => {
      if (pathData.pluginId) {
        if (!pluginsMap.has(pathData.pluginId)) {
          pluginsMap.set(pathData.pluginId, {
            pluginId: pathData.pluginId,
            pluginName: pathData.pluginName || pathData.pluginId,
            paths: [pathData],
          });
        } else {
          pluginsMap.get(pathData.pluginId)!.paths.push(pathData);
        }
      }
    });

    // Convert to array
    const pluginsArray = Array.from(pluginsMap.values()).map((p) => ({
      pluginId: p.pluginId,
      pluginName: p.pluginName,
      pathCount: p.paths.length,
      hasDuplicateName: false,
    }));

    // Detect duplicate plugin names
    const nameCount = new Map<string, number>();
    pluginsArray.forEach((p) => {
      nameCount.set(p.pluginName, (nameCount.get(p.pluginName) || 0) + 1);
    });

    // Mark plugins with duplicate names
    availablePlugins = pluginsArray.map((p) => ({
      ...p,
      hasDuplicateName: (nameCount.get(p.pluginName) || 0) > 1,
    }));

    // Get available pages for selected plugin (optionally filtered by version)
    if (config?.pluginId) {
      const pluginData = pluginsMap.get(config.pluginId);
      if (pluginData) {
        // Start with all pages for this plugin
        let pages = pluginData.paths;

        // If a version is selected, filter pages to only those available in that version
        if (config.instanceVersion) {
          pages = pages.filter((pd: any) => {
            // Check if this page has the selected version
            return pd.versions?.some((v: any) => v.version === config.instanceVersion);
          });
        }

        availablePages = pages.map((pd: any) => ({
          path: pd.path,
          name: pd.name,
          remappable: pd.remappable,
          screenshots: pd.screenshots,
        }));
      }

      // Get versions and configs for selected plugin + page
      const selectedPath = config.metadata?.internalPath || availablePages[0]?.path;
      const matchingPathData = instancesData.byPath.find(
        (pd: any) => pd.pluginId === config.pluginId && pd.path === selectedPath,
      );

      if (matchingPathData?.versions) {
        availableVersions = matchingPathData.versions.map((v: any) => v.version);
        const configsMap = new Map<string, Set<string>>();
        const configPreviewMap = new Map<string, boolean>();
        matchingPathData.versions.forEach((v: any) => {
          v.configs?.forEach((c: any) => {
            if (!configsMap.has(c.configName)) {
              configsMap.set(c.configName, new Set());
            }
            configsMap.get(c.configName)!.add(v.version);
            // Track if this config is a preview
            if (c.isPreview) {
              configPreviewMap.set(c.configName, true);
            }
          });
        });
        // All configs are permanent now - no filtering needed
        availableConfigs = Array.from(configsMap.entries()).map(([configName, versions]) => ({
          configName,
          versions: Array.from(versions),
        }));
      }
    }
  }

  // Auto-validate and fix invalid selections
  // NOTE: We only validate when there's NO instanceId - if instanceId exists, it's a valid deployed config
  // This prevents the UI from clearing valid configs just because they're not in the dropdown list
  React.useEffect(() => {
    if (!instancesData?.byPath || !config) return;

    // If config already has instanceId, it's deployed and valid - don't auto-clear
    // This prevents clearing configs for paths that aren't in the dropdown (e.g. offer pages with checkout design)
    if (config.instanceId) {
      return;
    }

    let needsUpdate = false;
    const updates: any = {};

    // Validate pluginId
    if (config.pluginId && !availablePlugins.find((p) => p.pluginId === config.pluginId)) {
      needsUpdate = true;
      updates.pluginId = undefined;
      updates.instanceVersion = undefined;
      updates.variant = undefined;
      updates.instanceId = undefined;
      updates.metadata = { ...config.metadata, internalPath: undefined };
    }

    // Validate page (internalPath)
    if (config.pluginId && config.metadata?.internalPath) {
      const validPage = availablePages.find((p) => p.path === config.metadata?.internalPath);
      if (!validPage) {
        needsUpdate = true;
        updates.instanceVersion = undefined;
        updates.variant = undefined;
        updates.instanceId = undefined;
        updates.metadata = { ...(config.metadata || {}), internalPath: undefined };
      }
    }

    // Validate version
    if (config.pluginId && config.instanceVersion && !availableVersions.includes(config.instanceVersion)) {
      needsUpdate = true;
      updates.instanceVersion = undefined;
      updates.variant = undefined;
      updates.instanceId = undefined;
    }

    // Validate config variant (only if no instanceId - meaning user is selecting, not viewing existing)
    if (config.pluginId && config.instanceVersion && config.variant && !config.instanceId) {
      const validConfig = availableConfigs.find((c) => c.configName === config.variant);
      if (!validConfig || !validConfig.versions.includes(config.instanceVersion)) {
        needsUpdate = true;
        updates.variant = undefined;
        updates.instanceId = undefined;
      }
    }

    if (needsUpdate) {
      handleConfigChange(updates);
    }
  }, [
    config,
    config?.pluginId,
    config?.metadata?.internalPath,
    config?.instanceVersion,
    config?.variant,
    availablePlugins,
    availablePages,
    availableVersions,
    availableConfigs,
    instancesData,
  ]);

  // Auto-set internalPath for single-page plugins
  React.useEffect(() => {
    if (config?.pluginId && !config?.metadata?.internalPath && availablePages.length === 1) {
      // Only one page available, auto-select it
      handleConfigChange({
        metadata: {
          ...config.metadata,
          internalPath: availablePages[0].path,
        },
      });
    }
  }, [config?.pluginId, config?.metadata?.internalPath, availablePages]);

  // Auto-select latest semantic version when page is selected
  React.useEffect(() => {
    if (
      config?.pluginId &&
      config?.metadata?.internalPath &&
      !config?.instanceVersion &&
      availableVersions.length > 0
    ) {
      // Helper to parse semantic version
      const parseVersion = (version: string) => {
        const parts = version.split('.').map(Number);
        return {
          major: parts[0] || 0,
          minor: parts[1] || 0,
          patch: parts[2] || 0,
        };
      };

      // Find latest semantic version
      const latestVersion = availableVersions.reduce((latest, current) => {
        const latestParsed = parseVersion(latest);
        const currentParsed = parseVersion(current);

        if (currentParsed.major > latestParsed.major) return current;
        if (currentParsed.major < latestParsed.major) return latest;
        if (currentParsed.minor > latestParsed.minor) return current;
        if (currentParsed.minor < latestParsed.minor) return latest;
        if (currentParsed.patch > latestParsed.patch) return current;
        return latest;
      }, availableVersions[0]);

      handleConfigChange({
        instanceVersion: latestVersion,
      });
    }
  }, [config?.pluginId, config?.metadata?.internalPath, config?.instanceVersion, availableVersions]);

  // Auto-select the most recent design when design selector is hidden
  // This applies to builder plugins or when ui.hideDesignSelector is set
  React.useEffect(() => {
    if (
      config?.pluginId &&
      config?.metadata?.internalPath &&
      config?.instanceVersion &&
      !config?.variant && // No design selected yet
      !config?.instanceId && // Not already deployed
      availableConfigs.length > 0
    ) {
      // Check if design selector should be hidden
      const selectedPathData = instancesData?.byPath?.find(
        (p: any) => p.pluginId === config.pluginId && p.path === config.metadata?.internalPath
      ) as any;

      const isBuilderPlugin = selectedPathData?.pageType === 'builder';
      const hideDesignSelector = selectedPathData?.ui?.hideDesignSelector;

      if (isBuilderPlugin || hideDesignSelector) {
        // Find the most recent design by looking at the configs
        // Configs are typically ordered by creation time, but we can also look for timestamps
        const matchingPathData = instancesData?.byPath?.find(
          (pd: any) => pd.pluginId === config.pluginId && pd.path === config.metadata?.internalPath
        );

        if (matchingPathData?.versions) {
          const matchingVersion = matchingPathData.versions.find(
            (v: any) => v.version === config.instanceVersion
          );

          if (matchingVersion?.configs && matchingVersion.configs.length > 0) {
            // Sort by updatedAt (most recent first) if available, otherwise use last in array
            const sortedConfigs = [...matchingVersion.configs].sort((a: any, b: any) => {
              if (a.updatedAt && b.updatedAt) {
                return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
              }
              return 0; // Keep original order if no timestamps
            });

            const mostRecentConfig = sortedConfigs[0];

            handleConfigChange({
              variant: mostRecentConfig.configName,
              instanceId: mostRecentConfig.instanceId,
            });
          }
        }
      }
    }
  }, [
    config?.pluginId,
    config?.metadata?.internalPath,
    config?.instanceVersion,
    config?.variant,
    config?.instanceId,
    availableConfigs,
    instancesData,
  ]);

  // Reset page selection when version changes and current page is not available.
  // Skip when the step already has a deployed instance — the deployed config is
  // authoritative even if its internalPath uses routing template params like
  // /thankyou/:orderId that the plugin manifest's bare page list (`/thankyou`)
  // won't match. Resetting in that case kicks off the auto-select-design
  // effect, which then flags instanceChanged and wipes previewScreenshot.
  React.useEffect(() => {
    if (config?.instanceId) return;
    if (
      config?.pluginId &&
      config?.instanceVersion &&
      config?.metadata?.internalPath &&
      availablePages.length > 0
    ) {
      // Check if current page is still available in the selected version
      const pageStillAvailable = availablePages.some((p) => p.path === config.metadata?.internalPath);

      if (!pageStillAvailable) {
        // Reset to first available page and clear config/instance
        handleConfigChange({
          metadata: { ...config.metadata, internalPath: availablePages[0]?.path },
          variant: undefined,
          instanceId: undefined,
        });
      }
    }
  }, [config?.instanceId, config?.pluginId, config?.instanceVersion, availablePages, config?.metadata?.internalPath]);

  // ─── Build designs array for DesignPicker (must be before early return - React hook rules) ─
  const activeInstanceId = config?.instanceId;
  const activeInstanceVersion = config?.instanceVersion;
  // WHICH page of that instance this step points at. A multi-page plugin
  // deploys ONE instance for every page it serves, so `/club` and `/portal`
  // carry the same instanceId — the path is what tells them apart (TAG-2021).
  const activeInternalPath = stripRouteParams(config?.metadata?.internalPath || config?.pagePath);

  // The funnel-step preview capture (node.metadata.previewScreenshot, written
  // by the screenshot inngest job) for the currently-selected step/variant.
  // This is a shot of the ACTUAL deployed page for this step — the same image
  // the flow canvas shows — so the active design card prefers it over the
  // generic deployment demoPreviews (which are a client-side editor render,
  // shared across every config of the deployment, and can be stale).
  // Failed captures store /placeholder.jpg with isPlaceholder: true; treat
  // those as "no image" so the card falls back instead of showing the
  // placeholder as if it were the design.
  const activeStepScreenshot: { url: string; mobileUrl?: string } | undefined = React.useMemo(() => {
    const shot = isSplitStep && step.variants?.[selectedVariantIndex]
      ? (step.metadata as any)?.variantScreenshots?.[step.variants[selectedVariantIndex].id]
      : (step.metadata as any)?.previewScreenshot;
    return shot?.url && !shot?.isPlaceholder
      ? { url: shot.url, mobileUrl: shot.mobileUrl }
      : undefined;
  }, [isSplitStep, step.variants, step.metadata, selectedVariantIndex]);
  const activeStepScreenshotUrl = activeStepScreenshot?.url;

  // A newer design save (designSavedAt) than the displayed capture means a
  // recapture is in flight — surface it as the "updating" overlay on the
  // active card (mirrors FlowStepNode.isRecapturePending). A capture-error
  // marker newer than the save clears it so we don't imply progress forever.
  const isRecapturePending = React.useMemo(() => {
    const meta: any = step.metadata || {};
    const capturedAtRaw = isSplitStep && step.variants?.[selectedVariantIndex]
      ? meta.variantScreenshots?.[step.variants[selectedVariantIndex].id]?.capturedAt
      : meta.previewScreenshot?.capturedAt;
    const savedAt: number | undefined = meta.designSavedAt;
    if (!savedAt || !capturedAtRaw) return false;
    const capturedMs = Date.parse(capturedAtRaw);
    if (Number.isNaN(capturedMs) || savedAt <= capturedMs) return false;
    const errFailedAt = isSplitStep && step.variants?.[selectedVariantIndex]
      ? meta.variantScreenshotErrors?.[step.variants[selectedVariantIndex].id]?.failedAt
      : meta.previewScreenshotError?.failedAt;
    if (errFailedAt && Date.parse(errFailedAt) > savedAt) return false;
    return true;
  }, [step.metadata, isSplitStep, step.variants, selectedVariantIndex]);

  const designs: DesignOption[] = React.useMemo(() => {
    if (!instancesData?.byPath) return [];

    const result: DesignOption[] = [];

    const parseSemver = (v: string) => {
      const parts = v.split('.').map(Number);
      return { major: parts[0] || 0, minor: parts[1] || 0, patch: parts[2] || 0 };
    };
    const compareSemver = (a: string, b: string) => {
      const pa = parseSemver(a), pb = parseSemver(b);
      if (pa.major !== pb.major) return pb.major - pa.major;
      if (pa.minor !== pb.minor) return pb.minor - pa.minor;
      return pb.patch - pa.patch;
    };

    const seen = new Set<string>();

    instancesData.byPath.forEach((pathData: any) => {
      if (!pathData?.pluginId || !pathData?.versions?.length) return;

      const sortedVersions = [...pathData.versions].sort((a: any, b: any) =>
        compareSemver(a.version, b.version)
      );
      const latestVersion = sortedVersions[0];
      const otherVersions = sortedVersions.slice(1).map((v: any) => v.version);
      const editorType = pathData.editor?.type || null;
      const editorUrl = pathData.editor?.url || undefined;

      const pageId = pathData.id;

      // If the user switched to a non-latest version, use that version's config
      const activeVersion = activeInstanceVersion
        ? sortedVersions.find((v: any) => v.version === activeInstanceVersion)
        : null;
      const displayVersion = activeVersion || latestVersion;

      if (displayVersion?.configs) {
        displayVersion.configs.forEach((cfg: any) => {
          // Match by path, not pageId — pageId isn't always stored in step
          // config metadata (e.g. auto-deploy from FunnelTemplateDialog),
          // but internalPath/pagePath always is. Keeps selectedKey alignment.
          const key = `${pathData.pluginId}:${pathData.path}:${cfg.configName || cfg.instanceId}`;
          if (seen.has(key)) return;
          seen.add(key);

          // Each design uses its own instance's screenshot, not the active step selection
          const inst = instancesData.instances?.find(
            (i: any) => i.instanceId === cfg.instanceId && i.path === pathData.path
          );

          // Path-level fallback from demoPreviews (same for all configs of this path)
          const pathScreenshot = pathData.screenshots?.desktop || pathData.screenshots?.mobile;

          // For the design currently mounted on this step, the funnel-step
          // capture of the real deployed page is the most truthful image (and
          // matches the flow-canvas thumbnail), so it RANKS FIRST for the
          // active card; other designs only have deployment demoPreviews.
          // Path-scoped: without it every page of a multi-page instance claims
          // the step's capture, so sibling pages show each other's screenshot.
          const isActiveDesign = !!activeInstanceId
            && cfg.instanceId === activeInstanceId
            && (!activeInternalPath || stripRouteParams(pathData.path) === activeInternalPath);

          // Derive this design's deployment from its OWN instanceId
          // (`${deploymentId}-${configHash}`) so Edit/open targets the exact
          // deployment this instance runs. The previous fallback chain landed on
          // `pathData.deploymentId` (the path-level default — often an older
          // version) even when the card shows the newer version, so clicking
          // Edit reopened the wrong/stale version (TAG-1334).
          const cfgDashIdx = (cfg.instanceId || '').indexOf('-');
          const instDeploymentId = cfgDashIdx > 0
            ? cfg.instanceId.slice(0, cfgDashIdx)
            : (cfg.deploymentId || displayVersion.deploymentId || pathData.deploymentId);

          result.push({
            key,
            instanceId: cfg.instanceId,
            pageId,
            name: pathData.pageName || cfg.configName || pathData.pluginName || 'Design',
            configName: cfg.configName,
            pluginName: pathData.pluginName,
            screenshotUrl: (isActiveDesign ? activeStepScreenshotUrl : undefined) || inst?.screenshots?.desktop || inst?.screenshots?.mobile || pathScreenshot,
            screenshotMobileUrl: (isActiveDesign ? activeStepScreenshot?.mobileUrl : undefined) || inst?.screenshots?.mobile || pathData.screenshots?.mobile,
            pluginId: pathData.pluginId,
            deploymentId: instDeploymentId,
            pagePath: pathData.path,
            version: latestVersion.version,
            otherVersions,
            updatedAt: cfg.updatedAt || cfg.createdAt,
            createdAt: pathData.pluginCreatedAt,
            editorType,
            editorUrl,
          });
        });
      }
    });

    result.sort((a, b) => {
      const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return bTime - aTime;
    });

    return result;
  }, [instancesData, activeInstanceId, activeInstanceVersion, activeInternalPath, activeStepScreenshot, activeStepScreenshotUrl]);

  // (contextual visibility removed — always show all relevant settings)

  // Meta pixels this step already fires, so the scripts editor can flag a custom
  // script that fires the same events again.
  const metaPixelsRaw = isSplitStep
    ? (step.variants?.[selectedVariantIndex]?.config?.stepConfig as any)?.pixels?.facebook
    : (config?.stepConfig as any)?.pixels?.facebook;
  const managedMetaPixels = React.useMemo<ManagedMetaPixel[]>(() => {
    if (!Array.isArray(metaPixelsRaw)) return [];
    return metaPixelsRaw
      .filter((pixel: any) => pixel?.enabled && pixel?.pixelId)
      .map((pixel: any) => ({
        pixelId: String(pixel.pixelId).trim(),
        events: Object.entries(pixel.events ?? {})
          .filter(([, isOn]) => isOn === true)
          .map(([event]) => event),
      }));
  }, [metaPixelsRaw]);

  // Early return if no config
  if (!config) return null;



  // Handle changes (works for both split and standard steps)
  const handleConfigChange = (updates: Partial<typeof config>) => {
    // Clear stale previewScreenshot when instance changes (old screenshot was for previous design)
    const instanceChanged =
      updates.instanceId !== undefined && updates.instanceId !== config?.instanceId;
    const clearScreenshotMeta = instanceChanged
      ? (meta: Record<string, any> | undefined) => {
          if (!meta) return meta;
          if (isSplitStep && step.variants?.[selectedVariantIndex]) {
            const variantId = step.variants[selectedVariantIndex].id;
            const { [variantId]: _, ...rest } = meta.variantScreenshots || {};
            return { ...meta, variantScreenshots: rest };
          }
          return { ...meta, previewScreenshot: undefined };
        }
      : (m: Record<string, any> | undefined) => m;

    if (isSplitStep && step.variants) {
      const updatedVariants = step.variants.map((v, idx) =>
        idx === selectedVariantIndex ? { ...v, config: { ...v.config, ...updates } } : v,
      );
      onChange({
        ...step,
        variants: updatedVariants,
        metadata: clearScreenshotMeta(step.metadata),
      });
    } else {
      onChange({
        ...step,
        config: { ...config, ...updates },
        metadata: clearScreenshotMeta(step.metadata),
      });
    }
  };

  // Handle path changes - updates ALL variants since they must share the same path for A/B testing
  const handlePathChange = (updates: Partial<typeof config>) => {
    if (isSplitStep && step.variants) {
      // Update path for ALL variants (they must share the same URL for A/B routing)
      const updatedVariants = step.variants.map((v) => ({
        ...v,
        config: { ...v.config, ...updates },
      }));
      onChange({ ...step, variants: updatedVariants });
    } else {
      onChange({
        ...step,
        config: { ...config, ...updates },
      });
    }
  };

  // Config editor handlers removed — Studio is the only editor now
  // Edit is handled via onOpenVisualEditor prop (opens Studio iframe)

  // Handle Delete Design - Delete any design permanently
  const handleDiscardDraft = (designInstanceId: string) => {
    setInstanceToDiscard(designInstanceId);
  };

  // Confirm delete design
  const confirmDiscardInstance = () => {
    if (!instanceToDiscard) return;
    removeInstanceMutation.mutate({
      instanceId: instanceToDiscard,
      storeId: id || '',
    });
  };

  // Handle rename design
  const handleRenameDesign = async () => {
    if (!instanceToRename || !newDesignName.trim()) return;

    try {
      // Fetch the current instance to get its full config
      const instanceData = await utils.plugins.getInstanceById.fetch({
        instanceId: instanceToRename.instanceId,
      });

      if (!instanceData) {
        toast({
          title: t('stepEditor.error'),
          description: t('stepEditor.instanceNotFound'),
          variant: 'destructive',
        });
        return;
      }

      // Update the config with new configName while preserving all other fields
      renameInstanceMutation.mutate({
        instanceId: instanceToRename.instanceId,
        config: {
          ...instanceData.config,
          configName: newDesignName.trim(),
        },
      });
    } catch (error) {
      console.error('[handleRenameDesign] Error:', error);
      toast({
        title: t('stepEditor.error'),
        description: t('stepEditor.failedFetchInstance'),
        variant: 'destructive',
      });
    }
  };

  // designs memo is now above the early return (React hook rules)

  // ─── Contextual visibility ────────────────────────────────────────
  const hasDesign = !!config?.instanceId;
  const isEntryStep = !!(step.isDefault || step.conditions?.entry);

  // ─── Badge helpers ────────────────────────────────────────────────
  const stepResources = isSplitStep
    ? { ...step.variants?.[selectedVariantIndex]?.config?.stepConfig?.staticResources, ...step.variants?.[selectedVariantIndex]?.config?.stepConfig?.resources }
    : { ...config?.stepConfig?.staticResources, ...config?.stepConfig?.resources };
  const resourceCount = stepResources ? Object.keys(stepResources).filter(k => stepResources[k]).length : 0;
  const hasResources = resourceCount > 0;
  // Hide for checkout (has dedicated Order Bumps section).
  // Show for offer steps (manifest-driven offer picker), landing, and custom.
  const showResourceBindings = step.type !== 'checkout'
    && (hasResources || (hasDesign && ['landing', 'custom', 'offer'].includes(step.type)));

  const scripts_ = isSplitStep
    ? step.variants?.[selectedVariantIndex]?.config?.stepConfig?.scripts
    : config?.stepConfig?.scripts;
  const scriptCount = Array.isArray(scripts_) ? scripts_.filter((s: any) => s?.enabled !== false).length : 0;
  const hasScripts = scriptCount > 0;
  // Surfaced on the section header: the merchants who pasted a duplicate pixel
  // have no reason to reopen Custom Scripts, and the section is collapsed by
  // default, so a warning that only lives inside it never reaches them.
  const metaConflictCount = Array.isArray(scripts_)
    ? countMetaScriptConflicts(scripts_, managedMetaPixels)
    : 0;

  const trackingConfig = isSplitStep
    ? (step.variants?.[selectedVariantIndex]?.config?.stepConfig as any)?.tracking
    : (config?.stepConfig as any)?.tracking;
  const hasPixels = trackingConfig ? Object.values(trackingConfig).some((v: any) => v && v !== false) : false;

  const paymentFlowId = isSplitStep
    ? step.variants?.[selectedVariantIndex]?.config?.stepConfig?.payment?.paymentFlowId
    : config?.stepConfig?.payment?.paymentFlowId;
  const effectivePaymentFlowId = paymentFlowId || store?.selectedPaymentFlowId || undefined;

  const paymentSetupConfig = isSplitStep
    ? step.variants?.[selectedVariantIndex]?.config?.stepConfig?.paymentSetupConfig
    : config?.stepConfig?.paymentSetupConfig;
  const enabledMethodCount = paymentSetupConfig
    ? Object.values(paymentSetupConfig).filter((m) => m.enabled).length
    : undefined;

  const shopifyAvailable = templatesWithIntegrationStatus?.some(
    (t: any) =>
      t.id === "shopify-default" &&
      t.integrationStatus?.connected
  ) ?? false;

  const wooAvailable = templatesWithIntegrationStatus?.some(
    (t: any) =>
      t.id === "woocommerce-default" &&
      t.integrationStatus?.connected
  ) ?? false;

  // Studio-builder pages are the only runtime that consumes
  // shippingRateSettings / addressSettings (filters live in studio-app
  // chunks + tagada-resolver). Direct-mode templates (tagada-native,
  // shopify-default, woocommerce-default, etc.) declare no `editor`
  // field in their plugin manifest, so use the active design's manifest
  // editor as the positive marker — studio plugins always set it.
  const activePathData = (instancesData?.byPath as any[] | undefined)?.find(
    (pd) =>
      pd?.pluginId === config?.pluginId &&
      pd?.path === (config?.metadata?.internalPath || config?.pagePath),
  );
  const isStudioPage = !!activePathData?.editor?.type;

  return (
    <div className="flex h-full flex-col bg-slate-50 dark:bg-slate-950">
      {/* ─── Header ────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-40 border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
        {/* Row 1: Back + Name + A/B badge + Type selector */}
        <div className="flex items-center gap-1.5 px-3 py-1.5">
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
            title={t('stepEditor.closeEditor')}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </button>
          <Input
            value={labelDraft ?? step.label}
            onChange={(e) => setLabelDraft(e.target.value)}
            onBlur={() => {
              if (labelDraft !== null && labelDraft !== step.label) {
                onChange({ ...step, label: labelDraft });
              }
              setLabelDraft(null);
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            placeholder={t('stepEditor.pageNamePlaceholder')}
            className="h-7 min-w-0 flex-1 border-transparent bg-transparent px-1 text-sm font-semibold shadow-none focus-visible:border-slate-200 focus-visible:bg-white dark:focus-visible:border-slate-700 dark:focus-visible:bg-slate-900"
          />
          {isSplitStep && (
            <span className="flex shrink-0 items-center gap-1 rounded-full bg-purple-50 px-1.5 py-0.5 text-[10px] font-medium text-purple-600 dark:bg-purple-950/40 dark:text-purple-400">
              <Split className="h-2.5 w-2.5" />
              A/B
            </span>
          )}
          <Select
                value={step.type}
                onValueChange={(value: StepType) => { onChange({ ...step, type: value }); }}
              >
                <SelectTrigger className="h-7 w-auto shrink-0 gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 [&_svg]:size-3 [&_span_.flex_svg]:text-primary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>{t('stepEditor.pageTypes')}</SelectLabel>
                    {(Object.entries(STEP_TYPE_LABELS) as [StepType, string][]).map(
                      ([type, label]) => {
                        const TypeIcon = STEP_ICONS[type];
                        const isShopifyStep = type.includes('shopify');
                        const isWooStep = type.includes('woocommerce');
                        const isDisabled =
                          (isShopifyStep && !shopifyAvailable) ||
                          (isWooStep && !wooAvailable);
                        return (
                          <SelectItem key={type} value={type} disabled={isDisabled}>
                            <div className="flex items-center gap-2">
                              <TypeIcon className="h-3.5 w-3.5" />
                              <span className={isDisabled ? 'opacity-50' : ''}>{label}</span>
                            </div>
                          </SelectItem>
                        );
                      }
                    )}
                  </SelectGroup>
                </SelectContent>
              </Select>
        </div>

        {/* Row 2: URL Path — always visible when a design is mounted */}
        {step.type !== 'external' && config?.instanceId && currentInstanceData && (
          <div className="flex items-center border-t border-slate-200 px-1.5 py-1 dark:border-slate-700">
            <div className="min-w-0 flex-1">
              <PathEditor
                value={config.pagePath || currentInstanceData.path}
                disabled={!remappableStatus?.remappable}
                className="!min-h-0 !rounded-md !border-0 !bg-transparent !px-1.5 !py-0.5 !shadow-none"
                onChange={(newPath: string) => {
                  const internalPath = config.metadata?.internalPath || currentInstanceData.path;
                  const customPath = newPath !== internalPath;
                  handlePathChange({
                    pagePath: newPath,
                    metadata: {
                      ...config.metadata,
                      customPath: customPath ? newPath : undefined,
                      internalPath,
                      pathRemap: customPath ? { from: internalPath, to: newPath } : undefined,
                      routeType: customPath ? 'external' as const : undefined,
                    },
                  });
                }}
              />
            </div>
            {!remappableStatus?.remappable && (
              <span className="shrink-0 text-[9px] text-slate-400">{t('stepEditor.locked')}</span>
            )}
          </div>
        )}
      </div>

      {/* ─── Variant Picker (top-level, applies to all sections below) ── */}
      {isSplitStep && step.variants && (
        <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
          <div className="flex items-center gap-1.5">
            <div className="flex flex-1 items-center gap-0.5 rounded-lg bg-white p-0.5 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
              {step.variants.map((variant, idx) => {
                const isActive = selectedVariantIndex === idx;
                return (
                  <button
                    key={variant.id}
                    onClick={() => setSelectedVariantIndex(idx)}
                    className={cn(
                      'flex-1 rounded-md px-2.5 py-1 text-[11px] font-medium transition-all',
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800',
                    )}
                  >
                    {variant.label}
                    {step.splitType === 'weighted' && (
                      <span className="ml-1 opacity-75">({variant.percentage}%)</span>
                    )}
                    {step.splitType === 'geo' && (
                      <span className="ml-1 opacity-75" title={variant.regions?.join(', ')}>
                        {formatRegionsSummary(variant.regions, t)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <ManageVariantsDialog
              step={step}
              onTransformToSplit={onTransformToSplit}
              onUpdateVariants={onUpdateVariants}
              onRemoveSplit={onRemoveSplit}
            >
              <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
                <Settings2 className="h-3 w-3" />
              </Button>
            </ManageVariantsDialog>
          </div>
        </div>
      )}

      {/* ─── Scrollable Content ──────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">

        {/* External step: URL config + tracker snippet */}
        {step.type === 'external' ? (
          <div className="space-y-0">
            {/* URL input */}
            <div className="border-b border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {t('stepEditor.pageUrl')}
              </Label>
              <Input
                value={config?.url || ''}
                onChange={(e) => handleConfigChange({ url: e.target.value })}
                placeholder="https://yoursite.com/landing"
                type="url"
                className="mt-2"
              />
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                {t('stepEditor.pageUrlVariables')}
              </p>
            </div>

            {/* Tracker setup */}
            <ExternalTrackerSnippet
              storeId={id || ''}
              accountId={store?.accountId || ''}
              funnelId={funnelId || ''}
              stepId={step.id}
              stepName={step.label}
              stepType={step.type}
              externalUrl={config?.url}
            />
          </div>
        ) : (
          <>
            {/* ─── Design Section (main working area) ────────────────── */}
            <div
              ref={designSectionRef}
              className={cn(
                'mx-3 my-3 rounded-lg border bg-white p-4 transition-all duration-300 dark:bg-slate-900',
                designHighlighted
                  ? 'border-primary ring-2 ring-primary/40 shadow-[0_0_20px_rgba(var(--primary-rgb,99,102,241),0.3)]'
                  : 'border-slate-200 dark:border-slate-700',
              )}
            >
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  {t('stepEditor.designs')}
                </h3>
                {isRefreshing && (
                  <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
                )}
              </div>

              {isInitialLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
              ) : (
                <DesignPicker
                  designs={designs}
                  confirmSwitch={!!isFunnelPublished && step.type === 'checkout' && !!config?.instanceId}
                  selectedKey={(() => {
                    if (!config?.pluginId) return undefined;
                    // Route params (e.g. `/thankyou/:orderId` → `/thankyou`) are
                    // stripped on both sides so config-side paths line up with
                    // design-side deployment paths (which are always clean).
                    const configPath = stripRouteParams(
                      config.metadata?.internalPath || config.pagePath || config.path,
                    );
                    const configVariant = (config.variant || '').toLowerCase();

                    // Strongest match: instanceId AND page path. instanceId
                    // alone is NOT unique per design — a multi-page plugin
                    // serves all of its pages from one instance, so `/club` and
                    // `/portal` share it. Matching on it alone pinned the
                    // "Active" card to whichever page sorted first, and picking
                    // any other page read as a dead click (TAG-2021).
                    let matched = config.instanceId
                      ? designs.find(
                        (d) => d.instanceId === config.instanceId
                          && stripRouteParams(d.pagePath) === configPath,
                      )
                      : undefined;

                    // No design carries this path (config path missing, or
                    // drifted from the deployment's). Fall back to instanceId
                    // alone — a possibly-imprecise card still beats the "no
                    // design selected" empty state on a step that has one.
                    if (!matched && config.instanceId) {
                      matched = designs.find((d) => d.instanceId === config.instanceId);
                    }

                    // Fallback: pluginId + clean path + case-insensitive variant.
                    if (!matched) {
                      matched = designs.find((d) => {
                        if (d.pluginId !== config.pluginId) return false;
                        if (stripRouteParams(d.pagePath) !== configPath) return false;
                        const dVariant = (d.configName || '').toLowerCase();
                        // If neither side has a variant, accept the match.
                        if (!configVariant && !dVariant) return true;
                        return configVariant === dVariant;
                      });
                    }

                    return matched?.key;
                  })()}
                  selectedVersion={config?.instanceVersion}
                  onSelect={(design) => {
                    const configUpdate: any = {
                      instanceId: design.instanceId,
                      pluginId: design.pluginId,
                      variant: design.configName,
                      instanceVersion: design.version,
                      pagePath: design.pagePath,
                      metadata: {
                        ...config?.metadata,
                        internalPath: design.pagePath,
                        pageId: design.pageId,
                        pluginId: design.pluginId,
                        deploymentId: design.deploymentId,
                      },
                    };

                    if (isSplitStep && step.variants && selectedVariantIndex > 0) {
                      const primaryPath = step.variants[0]?.config?.pagePath
                        || step.variants[0]?.config?.metadata?.internalPath;
                      if (primaryPath) {
                        configUpdate.pagePath = primaryPath;
                      }
                    }

                    if (isSplitStep && step.variants && selectedVariantIndex === 0) {
                      const updatedVariants = step.variants.map((v, idx) =>
                        idx === selectedVariantIndex
                          ? { ...v, config: { ...v.config, ...configUpdate } }
                          : { ...v, config: { ...v.config, pagePath: design.pagePath } },
                      );
                      onChange({ ...step, variants: updatedVariants });
                    } else {
                      handleConfigChange(configUpdate);
                    }
                  }}
                  onSelectVersion={(design, version) => {
                    const pathData = instancesData?.byPath?.find(
                      (pd: any) => pd.pluginId === design.pluginId && pd.path === design.pagePath,
                    );
                    const versionData = pathData?.versions?.find((v: any) => v.version === version);
                    const firstConfig = versionData?.configs?.[0];
                    if (firstConfig) {
                      // metadata.deploymentId MUST track the SELECTED version's
                      // deployment (the one `instanceId` was instantiated from), not
                      // the path-level default. Derive it from the same instanceId
                      // (`${deploymentId}-${configHash}`) so the editor pointer can
                      // NEVER lag behind the runtime instance. Previously this used
                      // pathData.deploymentId (the path default), so a version switch
                      // left Studio opening a stale version and re-deploying a version
                      // number that already existed → "won't save" + builder/studio
                      // version mismatch (TAG-1305).
                      const dashIdx = firstConfig.instanceId.indexOf('-');
                      const selectedDeploymentId = dashIdx > 0
                        ? firstConfig.instanceId.slice(0, dashIdx)
                        : (pathData?.deploymentId || design.deploymentId);
                      handleConfigChange({
                        instanceId: firstConfig.instanceId,
                        pluginId: design.pluginId,
                        instanceVersion: version,
                        variant: firstConfig.configName,
                        pagePath: design.pagePath,
                        metadata: {
                          ...config?.metadata,
                          internalPath: design.pagePath,
                          pageId: design.pageId,
                          pluginId: design.pluginId,
                          deploymentId: selectedDeploymentId,
                        },
                      });
                    }
                  }}
                  onCreateNew={onCreateWithStudio ? () => onCreateWithStudio(step.id) : undefined}
                  onBrowseMarketplace={() => setMarketplaceDialogOpen(true)}
                  onDuplicate={onDuplicateDesign ? () => onDuplicateDesign(step.id) : undefined}
                  onEdit={onEditDesign ? (designKey: string) => {
                    const design = designs.find(d => d.key === designKey);
                    // Split steps: tell the handler WHICH variant's design this
                    // is — fork-on-edit and the Studio save-back must target
                    // that variant's config (TAG-1334 gap b).
                    const editVariantId = isSplitStep ? step.variants?.[selectedVariantIndex]?.id : undefined;
                    onEditDesign(step.id, design ? {
                      pluginId: design.pluginId,
                      deploymentId: design.deploymentId,
                      pagePath: design.pagePath,
                    } : undefined, editVariantId);
                  } : undefined}
                  onDelete={(designKey) => {
                    const design = designs.find(d => d.key === designKey);
                    if (design) setInstanceToDiscard(design.instanceId);
                  }}
                  isLoading={isInitialLoading}
                  isCapturing={isCapturing || isRecapturePending}
                  stepType={step.type}
                />
              )}
              {isSplitStep && (
                <p className="mt-2 text-[10px] text-primary">
                  {t('stepEditor.abVariantsSharePath')}
                </p>
              )}
            </div>

            {/* Resource Bindings — manifest-driven (offer picker for offer steps, generic for landing/custom) */}
            <div {...(step.type === 'offer' ? { 'data-tour': 'offer-config' } : {})}>
            <CollapsibleSection
              title={step.type === 'offer' ? t('stepEditor.offerConfiguration') : t('stepEditor.resourceBindings')}
              icon={step.type === 'offer' ? ShoppingBag : Database}
              badge={hasResources ? t('stepEditor.resourceBindingsCount', { count: resourceCount }) : undefined}
              badgeVariant="info"
              defaultOpen={step.type === 'offer' || hasResources}
              visible={showResourceBindings}
            >
              <StepStaticResourcesEditor
                stepId={step.id}
                variantId={isSplitStep ? step.variants?.[selectedVariantIndex]?.id : undefined}
                funnelId={funnelId!}
                instanceId={config?.instanceId}
                storeId={id!}
                pagePath={config?.metadata?.internalPath}
                staticResources={
                  isSplitStep
                    ? { ...step.variants?.[selectedVariantIndex]?.config?.stepConfig?.staticResources, ...step.variants?.[selectedVariantIndex]?.config?.stepConfig?.resources }
                    : { ...config?.stepConfig?.staticResources, ...config?.stepConfig?.resources }
                }
                onResourceChange={(resourceType: string, resourceValue: any) => {
                  // Multi-binding resources (manifest `multiple: true`) come
                  // through as `string[]` and must persist verbatim — only
                  // single-binding `{id}` objects get unwrapped.
                  const resourceId = Array.isArray(resourceValue)
                    ? resourceValue
                    : typeof resourceValue === 'object' && resourceValue?.id
                      ? resourceValue.id
                      : resourceValue;

                  if (isSplitStep && step.variants) {
                    const currentResources =
                      { ...step.variants[selectedVariantIndex]?.config?.stepConfig?.staticResources, ...step.variants[selectedVariantIndex]?.config?.stepConfig?.resources };
                    const updatedVariants = step.variants.map((v, idx) =>
                      idx === selectedVariantIndex
                        ? {
                          ...v,
                          config: {
                            ...v.config,
                            stepConfig: {
                              ...v.config?.stepConfig,
                              resources: {
                                ...currentResources,
                                [resourceType]: resourceId,
                              },
                            },
                          },
                        }
                        : v,
                    );
                    onChange({ ...step, variants: updatedVariants });
                  } else {
                    handleConfigChange({
                      stepConfig: {
                        ...config?.stepConfig,
                        resources: {
                          ...config?.stepConfig?.staticResources,
                          ...config?.stepConfig?.resources,
                          [resourceType]: resourceId,
                        },
                      },
                    });
                  }
                }}
              />
            </CollapsibleSection>
            </div>

            {/* Order Bumps (checkout only) */}
            {step.type === 'checkout' && (
              <CollapsibleSection
                title={t('stepEditor.orderBumps')}
                icon={ShoppingBag}
                defaultOpen={false}
              >
                <OrderBumpStepConfig
                  storeId={id || ''}
                  value={isSplitStep
                    ? step.variants?.[selectedVariantIndex]?.config?.stepConfig?.orderBumps
                    : config?.stepConfig?.orderBumps
                  }
                  onChange={(newConfig) => {
                    if (isSplitStep && step.variants) {
                      const updatedVariants = step.variants.map((v, idx) =>
                        idx === selectedVariantIndex
                          ? {
                            ...v,
                            config: {
                              ...v.config,
                              stepConfig: {
                                ...v.config?.stepConfig,
                                orderBumps: newConfig,
                              },
                            },
                          }
                          : v,
                      );
                      onChange({ ...step, variants: updatedVariants });
                    } else {
                      handleConfigChange({
                        stepConfig: {
                          ...config?.stepConfig,
                          orderBumps: newConfig,
                        },
                      });
                    }
                  }}
                />
              </CollapsibleSection>
            )}

            {/* Checkout Offer Selector (checkout only — tiered offer picker) */}
            {step.type === 'checkout' && (
              <div data-tour="offer-config">
              <CollapsibleSection
                title={t('stepEditor.checkoutOffers')}
                icon={ShoppingBag}
                defaultOpen={false}
              >
                <CheckoutOfferStepConfig
                  storeId={id || ''}
                  value={isSplitStep
                    ? step.variants?.[selectedVariantIndex]?.config?.stepConfig?.checkoutOffers
                    : config?.stepConfig?.checkoutOffers
                  }
                  onChange={(newConfig) => {
                    if (isSplitStep && step.variants) {
                      const updatedVariants = step.variants.map((v, idx) =>
                        idx === selectedVariantIndex
                          ? {
                            ...v,
                            config: {
                              ...v.config,
                              stepConfig: {
                                ...v.config?.stepConfig,
                                checkoutOffers: newConfig,
                              },
                            },
                          }
                          : v,
                      );
                      onChange({ ...step, variants: updatedVariants });
                    } else {
                      handleConfigChange({
                        stepConfig: {
                          ...config?.stepConfig,
                          checkoutOffers: newConfig,
                        },
                      });
                    }
                  }}
                />
              </CollapsibleSection>
              </div>
            )}

            {/* Payment Methods (checkout only — card flow selector embedded) */}
            {step.type === 'checkout' && (
              <CollapsibleSection
                title={t('stepEditor.paymentMethods')}
                icon={CreditCard}
                badge={enabledMethodCount ? t('stepEditor.countEnabled', { count: enabledMethodCount }) : undefined}
                badgeVariant="success"
                defaultOpen={step.type === 'checkout'}
              >
                <PaymentSetupConfigEditor
                  storeId={id || ''}
                  paymentFlowId={effectivePaymentFlowId}
                  value={paymentSetupConfig}
                  onChange={(newConfig: Record<string, PaymentMethodEntry>) => {
                    if (isSplitStep && step.variants) {
                      const updatedVariants = step.variants.map((v, idx) =>
                        idx === selectedVariantIndex
                          ? {
                            ...v,
                            config: {
                              ...v.config,
                              stepConfig: {
                                ...v.config?.stepConfig,
                                paymentSetupConfig: newConfig,
                              },
                            },
                          }
                          : v,
                      );
                      onChange({ ...step, variants: updatedVariants });
                    } else {
                      handleConfigChange({
                        stepConfig: {
                          ...config?.stepConfig,
                          paymentSetupConfig: newConfig,
                        },
                      });
                    }
                  }}
                  paymentFlowValue={
                    isSplitStep
                      ? step.variants?.[selectedVariantIndex]?.config?.stepConfig?.payment?.paymentFlowId ||
                        step.variants?.[selectedVariantIndex]?.config?.paymentFlowId
                      : config?.stepConfig?.payment?.paymentFlowId || config?.paymentFlowId
                  }
                  onPaymentFlowChange={(flowId: string | undefined) => {
                    if (isSplitStep && step.variants) {
                      const updatedVariants = step.variants.map((v, idx) =>
                        idx === selectedVariantIndex
                          ? {
                            ...v,
                            config: {
                              ...v.config,
                              paymentFlowId: flowId,
                              stepConfig: {
                                ...v.config?.stepConfig,
                                payment: { ...v.config?.stepConfig?.payment, paymentFlowId: flowId },
                              },
                            },
                          }
                          : v,
                      );
                      onChange({ ...step, variants: updatedVariants });
                    } else {
                      handleConfigChange({
                        paymentFlowId: flowId,
                        stepConfig: {
                          ...config?.stepConfig,
                          payment: { ...config?.stepConfig?.payment, paymentFlowId: flowId },
                        },
                      });
                    }
                  }}
                />
              </CollapsibleSection>
            )}

            {/* Shipping Rates (studio-builder checkout pages only — chunks/resolver runtime is studio-app only) */}
            {step.type === 'checkout' && isStudioPage && (
              <CollapsibleSection
                title={t('stepEditor.shippingRates.title')}
                icon={Truck}
                defaultOpen={false}
              >
                <ShippingRatesStepConfig
                  storeId={id || ''}
                  value={
                    isSplitStep
                      ? step.variants?.[selectedVariantIndex]?.config?.stepConfig?.shippingRateSettings
                      : config?.stepConfig?.shippingRateSettings
                  }
                  onChange={(newConfig) => {
                    if (isSplitStep && step.variants) {
                      const updatedVariants = step.variants.map((v, idx) =>
                        idx === selectedVariantIndex
                          ? {
                            ...v,
                            config: {
                              ...v.config,
                              stepConfig: {
                                ...v.config?.stepConfig,
                                shippingRateSettings: newConfig,
                              },
                            },
                          }
                          : v,
                      );
                      onChange({ ...step, variants: updatedVariants });
                    } else {
                      handleConfigChange({
                        stepConfig: {
                          ...config?.stepConfig,
                          shippingRateSettings: newConfig,
                        },
                      });
                    }
                  }}
                />
              </CollapsibleSection>
            )}

            {/* Address Form (studio-builder checkout pages only — chunks/resolver runtime is studio-app only) */}
            {step.type === 'checkout' && isStudioPage && (
              <CollapsibleSection
                title={t('stepEditor.addressForm.title')}
                icon={MapPin}
                defaultOpen={false}
              >
                <AddressFormStepConfig
                  value={
                    isSplitStep
                      ? step.variants?.[selectedVariantIndex]?.config?.stepConfig?.addressSettings
                      : config?.stepConfig?.addressSettings
                  }
                  onChange={(newConfig) => {
                    if (isSplitStep && step.variants) {
                      const updatedVariants = step.variants.map((v, idx) =>
                        idx === selectedVariantIndex
                          ? {
                            ...v,
                            config: {
                              ...v.config,
                              stepConfig: {
                                ...v.config?.stepConfig,
                                addressSettings: newConfig,
                              },
                            },
                          }
                          : v,
                      );
                      onChange({ ...step, variants: updatedVariants });
                    } else {
                      handleConfigChange({
                        stepConfig: {
                          ...config?.stepConfig,
                          addressSettings: newConfig,
                        },
                      });
                    }
                  }}
                />
              </CollapsibleSection>
            )}

            {/* Access Restrictions (studio-builder checkout pages only — geo-block enforced at the serving edge) */}
            {step.type === 'checkout' && isStudioPage && (
              <CollapsibleSection
                title={t('stepEditor.accessRestrictions.title')}
                icon={Ban}
                defaultOpen={false}
              >
                <AccessRestrictionsStepConfig
                  value={
                    isSplitStep
                      ? step.variants?.[selectedVariantIndex]?.config?.stepConfig?.accessSettings
                      : config?.stepConfig?.accessSettings
                  }
                  onChange={(newConfig) => {
                    if (isSplitStep && step.variants) {
                      const updatedVariants = step.variants.map((v, idx) =>
                        idx === selectedVariantIndex
                          ? {
                            ...v,
                            config: {
                              ...v.config,
                              stepConfig: {
                                ...v.config?.stepConfig,
                                accessSettings: newConfig,
                              },
                            },
                          }
                          : v,
                      );
                      onChange({ ...step, variants: updatedVariants });
                    } else {
                      handleConfigChange({
                        stepConfig: {
                          ...config?.stepConfig,
                          accessSettings: newConfig,
                        },
                      });
                    }
                  }}
                />
              </CollapsibleSection>
            )}

            {/* Payment Translations (studio-builder checkout pages only) */}
            {step.type === 'checkout' && isStudioPage && (
              <CollapsibleSection
                title={t('stepEditor.paymentSettings.title')}
                icon={CreditCard}
                defaultOpen={false}
              >
                <PaymentSettingsStepConfig
                  value={
                    isSplitStep
                      ? step.variants?.[selectedVariantIndex]?.config?.stepConfig?.paymentSettings
                      : config?.stepConfig?.paymentSettings
                  }
                  onChange={(newConfig) => {
                    if (isSplitStep && step.variants) {
                      const updatedVariants = step.variants.map((v, idx) =>
                        idx === selectedVariantIndex
                          ? {
                            ...v,
                            config: {
                              ...v.config,
                              stepConfig: {
                                ...v.config?.stepConfig,
                                paymentSettings: newConfig,
                              },
                            },
                          }
                          : v,
                      );
                      onChange({ ...step, variants: updatedVariants });
                    } else {
                      handleConfigChange({
                        stepConfig: {
                          ...config?.stepConfig,
                          paymentSettings: newConfig,
                        },
                      });
                    }
                  }}
                />
              </CollapsibleSection>
            )}

            {/* Payment Configuration (offer/upsell steps only). Controls whether
                the post-checkout charge runs as MIT (off-session, no 3DS) or CIT
                (customer-initiated, may trigger 3DS). */}
            {step.type === 'offer' && (() => {
              const currentInitiator: 'merchant' | 'customer' =
                (isSplitStep
                  ? step.variants?.[selectedVariantIndex]?.config?.stepConfig?.paymentInitiator
                  : config?.stepConfig?.paymentInitiator) ?? 'merchant';

              const setInitiator = (next: 'merchant' | 'customer') => {
                if (isSplitStep && step.variants) {
                  const updatedVariants = step.variants.map((v, idx) =>
                    idx === selectedVariantIndex
                      ? {
                        ...v,
                        config: {
                          ...v.config,
                          stepConfig: {
                            ...v.config?.stepConfig,
                            paymentInitiator: next,
                          },
                        },
                      }
                      : v,
                  );
                  onChange({ ...step, variants: updatedVariants });
                } else {
                  handleConfigChange({
                    stepConfig: {
                      ...config?.stepConfig,
                      paymentInitiator: next,
                    },
                  });
                }
              };

              return (
                <CollapsibleSection
                  title={t('stepEditor.paymentConfiguration')}
                  icon={CreditCard}
                  badge={currentInitiator === 'customer' ? 'CIT' : 'MIT'}
                  badgeVariant={currentInitiator === 'customer' ? 'default' : 'success'}
                  defaultOpen={false}
                >
                  <div className="flex flex-col gap-2">
                    <Label className="text-sm font-medium">
                      {t('stepEditor.paymentInitiator')}
                    </Label>
                    <Select value={currentInitiator} onValueChange={(v) => setInitiator(v as 'merchant' | 'customer')}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="merchant">
                          {t('stepEditor.paymentInitiatorMit')}
                        </SelectItem>
                        <SelectItem value="customer">
                          {t('stepEditor.paymentInitiatorCit')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {t('stepEditor.paymentInitiatorHelp')}
                    </p>
                  </div>
                </CollapsibleSection>
              );
            })()}

            {/* Client-Side Tracking & Pixels */}
            {/* defaultOpen so the auto-seed effect inside TrackingConfigurationPanel
                actually mounts on every step. CollapsibleSection lazy-renders children;
                if collapsed the seeder useEffect never runs and the step saves with no
                pixels → runtime sees __TGD_STEP_CONFIG__.pixels=undefined → no scripts load. */}
            <CollapsibleSection
              title={t('stepEditor.clientSidePixels')}
              icon={Target}
              description={t('stepEditor.s2sTrackingDescription')}
              defaultOpen={true}
            >
              <TrackingConfigurationPanel
                step={step}
                config={config}
                isSplitStep={isSplitStep}
                selectedVariantIndex={selectedVariantIndex}
                onVariantSelect={setSelectedVariantIndex}
                onChange={onChange}
                handleConfigChange={handleConfigChange}
                storeId={id || ''}
                onCopyPixelsToAllSteps={onCopyPixelsToAllSteps}
              />
            </CollapsibleSection>

            {/* Custom Scripts */}
            <CollapsibleSection
              title={t('stepEditor.customScripts')}
              icon={Code2}
              badge={
                metaConflictCount > 0
                  ? t('scriptsEditor.metaDuplicateBadge', { count: metaConflictCount })
                  : hasScripts
                    ? t('stepEditor.scriptsCount', { count: scriptCount })
                    : undefined
              }
              badgeVariant={metaConflictCount > 0 ? 'warning' : 'default'}
              defaultOpen={false}
            >
              <ScriptsEditor
                scripts={
                  isSplitStep
                    ? step.variants?.[selectedVariantIndex]?.config?.stepConfig?.scripts || []
                    : config?.stepConfig?.scripts || []
                }
                managedMetaPixels={managedMetaPixels}
                onChange={(newScripts: any) => {
                  if (isSplitStep && step.variants) {
                    const updatedVariants = step.variants.map((v, idx) =>
                      idx === selectedVariantIndex
                        ? {
                          ...v,
                          config: {
                            ...v.config,
                            stepConfig: {
                              ...v.config?.stepConfig,
                              scripts: newScripts,
                            },
                          },
                        }
                        : v,
                    );
                    onChange({ ...step, variants: updatedVariants });
                  } else {
                    handleConfigChange({
                      stepConfig: {
                        ...config?.stepConfig,
                        scripts: newScripts,
                      },
                    });
                  }
                }}
              />
            </CollapsibleSection>

            {/* A/B Testing */}
            <CollapsibleSection
              title={t('stepEditor.abTesting')}
              icon={Split}
              badge={isSplitStep ? t('stepEditor.variantsCount', { count: step.variants?.length }) : undefined}
              badgeVariant="info"
              defaultOpen={false}
            >
              <div className="space-y-3">
                {!isSplitStep ? (
                  <div className="text-center">
                    <p className="mb-3 text-sm text-slate-500">
                      {t('stepEditor.createAbTestDescription')}
                    </p>
                    <ManageVariantsDialog
                      step={step}
                      onTransformToSplit={onTransformToSplit}
                      onUpdateVariants={onUpdateVariants}
                      onRemoveSplit={onRemoveSplit}
                    >
                      <Button variant="outline" size="sm" className="gap-1.5">
                        <Split className="h-3.5 w-3.5" />
                        {t('stepEditor.createSplitTest')}
                      </Button>
                    </ManageVariantsDialog>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500">
                      {step.splitType === 'weighted'
                        ? t('stepEditor.trafficSplitByWeight')
                        : t('stepEditor.trafficSplitByGeo')}
                    </p>
                    <ManageVariantsDialog
                      step={step}
                      onTransformToSplit={onTransformToSplit}
                      onUpdateVariants={onUpdateVariants}
                      onRemoveSplit={onRemoveSplit}
                    >
                      <Button variant="outline" size="sm" className="w-full gap-1.5">
                        <Settings2 className="h-3.5 w-3.5" />
                        {t('stepEditor.manageVariants')}
                      </Button>
                    </ManageVariantsDialog>
                  </div>
                )}
              </div>
            </CollapsibleSection>

            {/* Entry Point */}
            <CollapsibleSection
              title={t('stepEditor.entryPoint')}
              icon={Zap}
              badge={isEntryStep ? t('stepEditor.active') : undefined}
              badgeVariant="success"
              defaultOpen={isEntryStep}
            >
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">{t('stepEditor.funnelEntry')}</Label>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('stepEditor.customersCanEnter')}
                  </p>
                </div>
                <Switch
                  checked={step.isDefault || step.conditions?.entry || false}
                  onCheckedChange={(checked) => {
                    onChange({
                      ...step,
                      isDefault: checked,
                      conditions: {
                        ...step.conditions,
                        entry: checked,
                        entrySource: checked
                          ? step.conditions?.entrySource || { platform: 'direct' as const, channel: 'direct' as const }
                          : undefined,
                      },
                    });
                  }}
                />
              </div>
            </CollapsibleSection>

          </>
        )}
      </div>


      {/* Delete Design Dialog */}
      <AlertDialog open={!!instanceToDiscard} onOpenChange={(open) => {
        if (!open) {
          setInstanceToDiscard(null);
          setForceDeleteInfo(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {forceDeleteInfo ? t('stepEditor.designInUseTitle') : t('stepEditor.deleteDesignTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {forceDeleteInfo ? (
                <>
                  {t('stepEditor.designInUseAt')}{' '}
                  <span className="font-medium text-slate-700 dark:text-slate-300">{forceDeleteInfo.locations}</span>.
                  <br /><br />
                  {t('stepEditor.forceDeleteWarning')}
                </>
              ) : (
                t('stepEditor.deleteDesignDescription')
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setForceDeleteInfo(null)}>{t('stepEditor.cancel')}</AlertDialogCancel>
            {forceDeleteInfo ? (
              <Button
                onClick={() => {
                  removeInstanceMutation.mutate({
                    instanceId: forceDeleteInfo.instanceId,
                    storeId: id || '',
                    force: true,
                  });
                }}
                disabled={removeInstanceMutation.isPending}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {removeInstanceMutation.isPending ? t('stepEditor.forceDeleting') : t('stepEditor.forceDelete')}
              </Button>
            ) : (
              <Button
                onClick={() => instanceToDiscard && confirmDiscardInstance()}
                disabled={removeInstanceMutation.isPending}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {removeInstanceMutation.isPending ? t('stepEditor.deleting') : t('stepEditor.deleteDesign')}
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Mode Dialog removed — Studio is the only editor */}

      {/* Rename Design Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('stepEditor.renameDesignTitle')}</DialogTitle>
            <DialogDescription>{t('stepEditor.renameDesignDescription')}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              <Label htmlFor="new-design-name">{t('stepEditor.designName')}</Label>
              <Input
                id="new-design-name"
                value={newDesignName}
                onChange={(e) => setNewDesignName(e.target.value)}
                placeholder={t('stepEditor.enterNewDesignName')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newDesignName.trim()) {
                    handleRenameDesign();
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setRenameDialogOpen(false);
                setInstanceToRename(null);
                setNewDesignName('');
              }}
            >
              {t('stepEditor.cancel')}
            </Button>
            <Button
              onClick={handleRenameDesign}
              disabled={!newDesignName.trim() || renameInstanceMutation.isPending}
            >
              {renameInstanceMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('stepEditor.renaming')}
                </>
              ) : (
                t('stepEditor.rename')
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Store Dialog */}
      {marketplaceDialogOpen && (
        <Dialog open={marketplaceDialogOpen} onOpenChange={setMarketplaceDialogOpen}>
          <DialogContent className="flex max-h-[85vh] max-w-[90vw] flex-col overflow-hidden p-0 lg:max-w-5xl">
            <div className="border-b px-5 py-3">
              <h2 className="text-sm font-semibold">{t('stepEditor.importTemplate')}</h2>
              <p className="text-muted-foreground text-xs">{t('stepEditor.chooseTemplateForStep')}</p>
            </div>
            <div className="flex-1 overflow-y-auto">
              <PluginMarketplaceV2
                storeId={id || ''}
                contextFunnelId={funnelId}
                contextStepType={step.type as any}
                onInstallSuccess={(deploymentId, pluginName) => {
                  utils.plugins.getAvailableInstancesForStepType.invalidate();
                  toast({
                    title: t('stepEditor.pluginInstalled'),
                    description: t('stepEditor.pluginInstalledDescription', { pluginName }),
                  });
                  setMarketplaceDialogOpen(false);
                }}
                onOpenConfigEditor={() => {
                  setMarketplaceDialogOpen(false);
                }}
                onEditorClose={() => { }}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Config Editor Modal removed — Studio is the only editor */}

      {/* Step ID - Very Small at Bottom */}
      <div className="border-t border-slate-200 bg-white px-4 py-1.5 text-center dark:border-slate-700 dark:bg-slate-900">
        <p className="font-mono text-[9px] text-slate-400 dark:text-slate-600">Step ID: {step.id}</p>
      </div>
    </div>
  );
}

// Add default export
export default StepEditorPanel;
